import React, { useEffect, useRef, useState } from "react";
import { API, createCommande } from "../services/api";
import PatientForm from "./PatientForm";
import { SourceCommandeLabels } from "../models/sourceCommande.map";
import { useAuth } from "../auth/AuthContext";

const ACCEPTED_TYPES = ["application/pdf", "image/png", "image/jpeg", "application/vnd.openxmlformats-officedocument.wordprocessingml.document"];
const ACCEPTED_EXT = [".pdf", ".png", ".jpg", ".jpeg", ".docx"];
const DEFAULT_MAX_FILE_SIZE_MB = 30;
const SUPER_ADMIN_MAX_FILE_SIZE_MB = 100;

const inputStyle = {
    minHeight: "42px",
    padding: "9px 12px",
    borderRadius: "9px",
    border: "1px solid #B9C6C2",
    fontSize: "13px",
    outline: "none",
    color: "#122A26",
    background: "#FFFFFF",
    width: "100%",
    boxSizing: "border-box",
    fontFamily: "inherit"
};

const compactInputStyle = {
    ...inputStyle,
    minHeight: "36px",
    padding: "6px 10px",
    fontSize: "12px"
};

const labelStyle = {
    fontSize: "12px",
    fontWeight: 700,
    color: "#0C3B3C",
    display: "block",
    marginBottom: "5px"
};

function FileDropZone({ files, onChange, maxFileSizeMb }) {
    const [dragging, setDragging] = useState(false);
    const inputRef = useRef(null);

    const handleFiles = (incoming) => {
        const all = Array.from(incoming);
        const typeValid = all.filter(f =>
            ACCEPTED_TYPES.includes(f.type) ||
            ACCEPTED_EXT.some(ext => f.name.toLowerCase().endsWith(ext))
        );
        if (typeValid.length !== all.length) {
            alert("Seuls les fichiers PDF, PNG, JPG, JPEG et DOCX sont acceptés.");
        }
        const valid = typeValid.filter(f => {
            if (f.size > maxFileSizeMb * 1024 * 1024) {
                alert(`Le fichier "${f.name}" dépasse la limite de ${maxFileSizeMb} Mo.`);
                return false;
            }
            return true;
        });
        onChange(prev => {
            const existing = new Set(prev.map(f => f.name + f.size));
            return [...prev, ...valid.filter(f => !existing.has(f.name + f.size))];
        });
    };

    const onDrop = (e) => {
        e.preventDefault();
        setDragging(false);
        handleFiles(e.dataTransfer.files);
    };

    const removeFile = (idx) => onChange(prev => prev.filter((_, i) => i !== idx));

    return (
        <div>
            <button
                type="button"
                onDragOver={e => { e.preventDefault(); setDragging(true); }}
                onDragLeave={() => setDragging(false)}
                onDrop={onDrop}
                onClick={() => inputRef.current?.click()}
                style={{
                    border: `2px dashed ${dragging ? "#17B978" : "#B9C6C2"}`,
                    borderRadius: "10px",
                    padding: "16px",
                    textAlign: "center",
                    cursor: "pointer",
                    background: dragging ? "#E7F7EF" : "#FBFAF7",
                    transition: "border-color 0.15s, background 0.15s",
                    fontSize: "13px",
                    color: "#5A6D68",
                    width: "100%"
                }}
            >
                <div style={{ fontSize: "22px", marginBottom: "4px" }}>📎</div>
                <span>Glisser-déposer ou <strong style={{ color: "#0C3B3C" }}>parcourir</strong></span>
                <div style={{ fontSize: "11px", color: "#5A6D68", marginTop: "4px" }}>PDF, PNG, JPG, JPEG ou DOCX · max {maxFileSizeMb} Mo par fichier</div>
            </button>
            <input
                ref={inputRef}
                type="file"
                accept=".pdf,.png,.jpg,.jpeg,.docx"
                multiple
                style={{ display: "none" }}
                onChange={e => { handleFiles(e.target.files); e.target.value = ""; }}
            />

            {files.length > 0 && (
                <ul style={{ margin: "8px 0 0", padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: "4px" }}>
                    {files.map((f, idx) => (
                        <li key={idx} style={{
                            display: "flex", alignItems: "center", gap: "8px",
                            padding: "5px 10px",
                            background: "#F4F1EA",
                            borderRadius: "6px",
                            fontSize: "12px",
                            color: "#122A26"
                        }}>
                            <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{f.name}</span>
                            <span style={{ color: "#5A6D68", flexShrink: 0 }}>{(f.size / 1024).toFixed(0)} Ko</span>
                            <button
                                type="button"
                                onClick={() => removeFile(idx)}
                                aria-label={`Retirer ${f.name}`}
                                style={{ background: "none", border: "none", cursor: "pointer", color: "#5A6D68", fontSize: "14px", padding: "0 2px", lineHeight: 1 }}
                            >×</button>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

function validatePatientsForCreate(patients) {
    return patients.flatMap((p, index) => {
        const label = [p.nom, p.prenom].filter(Boolean).join(" ").trim() || `Patient ${index + 1}`;
        const errors = [];
        if (!p.nom?.trim()) errors.push(`${label} : nom obligatoire`);
        if (!p.prenom?.trim()) errors.push(`${label} : prénom obligatoire`);
        return errors;
    });
}

function transformPatientForCreate(p, defaultDestination = "ROBOT") {
    return {
        commandePatientId: p.commandePatientId ?? null,
        id: p.id ?? null,
        nom: p.nom,
        prenom: p.prenom,
        dateNaissance: p.dateNaissance || null,
        classification: p.classification || "UNKNOWN",
        commentaryProductionRobot: p.commentaryProductionRobot ?? null,
        commentaryProductionEtiquetage: p.commentaryProductionEtiquetage ?? null,
        identityResolution: p._identityResolution
            ?? (p._createdInCurrentDraft && p.id != null ? "UPDATE_EXISTING" : null),
        identityCandidateId: p._identityCandidateId ?? null,
        produits: (p.produits || []).map(prod => ({
            id: prod.id ?? null,
            nom: prod.nom ?? "",
            dosage: prod.dosage ?? "",
            posologie: prod.posologie ?? "",
            quantite: prod.quantite ?? 1,
            destination: prod.destination || defaultDestination,
            statut: prod.statut ?? null,
            siBesoin: prod.siBesoin ?? false,
            classificationMedicament: prod.classificationMedicament ?? null,
            deliveryDate: null,
            billingHour: null,
            billingValidated: false,
            billingUsername: null,
            planificationValidated: null,
            planificationValidatedAt: null,
            planificationValidatedBy: null,
            commentaryPlanificationRobot: null,
            commentaryPlanificationEtiquetage: null,
            ehpadWarnedIfOutOfStock: false,
        })),
    };
}

export { transformPatientForCreate, validatePatientsForCreate };

export default function CreateCommandeModal({ onClose, onCreated, medicamentStrategy, serviceKey }) {
    const { user } = useAuth();
    const maxFileSizeMb = user?.role === "SUPER_ADMIN" ? SUPER_ADMIN_MAX_FILE_SIZE_MB : DEFAULT_MAX_FILE_SIZE_MB;
    const [ehpads, setEhpads] = useState([]);
    const [ehpadId, setEhpadId] = useState("");
    const [tournee, setTournee] = useState("");
    const [emailEmetteur, setEmailEmetteur] = useState("");
    const [source, setSource] = useState("AUTRE");
    const [commentaire, setCommentaire] = useState("");
    const [notes, setNotes] = useState("");
    const [files, setFiles] = useState([]);
    const [patients, setPatients] = useState([]);
    const [createdCommandeId, setCreatedCommandeId] = useState(null);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const defaultMedicamentDestination = medicamentStrategy?.defaultDestination || "ROBOT";

    useEffect(() => {
        API.get("/ehpads").then(r => setEhpads(r.data)).catch(() => {});
    }, []);

    const handleEhpadChange = (id) => {
        setEhpadId(id);
        const found = ehpads.find(e => String(e.id) === id);
        setTournee(found?.tournee || "");
    };

    const handleSubmit = async () => {
        if (!ehpadId) return;
        if (patients.length > 0) {
            const patientErrors = validatePatientsForCreate(patients);
            if (patientErrors.length > 0) {
                setError(patientErrors.join(" · "));
                return;
            }
        }
        setSaving(true);
        setError(null);
        try {
            let commandeId = createdCommandeId;
            let savedCommande = null;

            if (!commandeId) {
                const res = await createCommande({
                    ehpadId: Number(ehpadId),
                    emailEmetteur: emailEmetteur.trim() || null,
                    commentaire: commentaire.trim() || null,
                    notes: notes.trim() || null,
                    source,
                    serviceKey: serviceKey || null,
                });
                commandeId = res.data.id;
                savedCommande = res.data;
                setCreatedCommandeId(commandeId);

                for (const file of files) {
                    const form = new FormData();
                    form.append("file", file);
                    await API.post(`/commandes/${commandeId}/ordonnances`, form, {
                        headers: { "Content-Type": "multipart/form-data" }
                    });
                }
            }

            if (patients.length > 0) {
                await API.post(`/commandes/${commandeId}/lock`, {}, { noRedirectOn401: true });
                try {
                    const patientsResponse = await API.patch(`/commandes/${commandeId}/patients`, {
                        ehpadId: Number(ehpadId),
                        patients: patients.map(p => ({
                            clientKey: p._clientKey,
                            patient: transformPatientForCreate(p, defaultMedicamentDestination)
                        }))
                    });
                    const results = patientsResponse.data?.results || [];
                    const failed = results.filter(r => !r.saved);
                    if (failed.length > 0) {
                        const msgs = failed.map(r => {
                            const failedPatient = patients.find(p => p._clientKey === r.clientKey);
                            const label = [failedPatient?.nom, failedPatient?.prenom].filter(Boolean).join(" ").trim() || "Patient";
                            return `${label} : ${r.error || "sauvegarde impossible"}`;
                        });
                        setError(`La commande a été créée, mais : ${msgs.join(" · ")}`);
                        setSaving(false);
                        return;
                    }
                    savedCommande = patientsResponse.data?.commande || savedCommande;
                } finally {
                    await API.delete(`/commandes/${commandeId}/lock`, { noRedirectOn401: true }).catch(() => {});
                }
            }

            onCreated(savedCommande || { id: commandeId });
        } catch (e) {
            const msg = e.response?.data?.message ?? e.response?.data?.detail ?? "Erreur lors de la création de la commande.";
            setError(msg);
            setSaving(false);
        }
    };

    return (
        <div className="create-order-overlay">
            <div role="dialog" aria-modal="true" aria-labelledby="create-order-title" className="create-order-modal">
                {/* Header */}
                <div className="create-order-header">
                    <div className="create-order-heading">
                        <span className="create-order-brand-mark" aria-hidden="true">
                            <img src="/symbole-couleur.svg" alt="" />
                        </span>
                        <div>
                            <h2 id="create-order-title">Nouvelle commande</h2>
                            <p>Enregistrez la demande et ses éléments de traitement.</p>
                        </div>
                    </div>
                    <button type="button" aria-label="Fermer" onClick={() => onClose()} className="create-order-close">✕</button>
                </div>

                {/* Form */}
                <div className="create-order-body">
                    <section className="create-order-section" aria-labelledby="create-order-information-title">
                        <div className="create-order-section-heading">
                            <span>01</span>
                            <div>
                                <h3 id="create-order-information-title">Informations de la commande</h3>
                                <p>Identifiez l’établissement et l’origine de la demande.</p>
                            </div>
                        </div>
                        <div className="create-order-grid create-order-grid--information">
                            <div className="create-order-field create-order-field--wide">
                            <label htmlFor="create-order-ehpad" style={labelStyle}>EHPAD <span style={{ color: "#B42318" }}>*</span></label>
                            <select
                                id="create-order-ehpad"
                                value={ehpadId}
                                onChange={e => handleEhpadChange(e.target.value)}
                                className="create-order-input"
                                style={{ ...compactInputStyle, cursor: "pointer" }}
                            >
                                <option value="">— Sélectionner —</option>
                                {ehpads.map(e => (
                                    <option key={e.id} value={e.id}>{e.nom}</option>
                                ))}
                            </select>
                            </div>
                            <div className="create-order-field">
                            <label htmlFor="create-order-tournee" style={labelStyle}>Tournée</label>
                            <input
                                id="create-order-tournee"
                                type="text"
                                value={tournee}
                                readOnly
                                className="create-order-input"
                                style={{ ...compactInputStyle, background: "#F4F1EA", color: "#5A6D68", cursor: "default" }}
                            />
                            </div>
                            <div className="create-order-field">
                            <label htmlFor="create-order-source" style={labelStyle}>Source de la commande</label>
                        <select
                            id="create-order-source"
                            value={source}
                            onChange={e => setSource(e.target.value)}
                            className="create-order-input"
                            style={{ ...compactInputStyle, cursor: "pointer" }}
                        >
                            {Object.entries(SourceCommandeLabels).map(([key, label]) => (
                                <option key={key} value={key}>{label}</option>
                            ))}
                        </select>
                            </div>
                            <div className="create-order-field">
                        <label htmlFor="create-order-email" style={labelStyle}>
                            <span>Adresse mail</span>
                            <span className="create-order-optional">Optionnel</span>
                        </label>
                        <input
                            id="create-order-email"
                            type="email"
                            value={emailEmetteur}
                            onChange={e => setEmailEmetteur(e.target.value)}
                            placeholder="Ex : contact@ehpad-exemple.fr"
                            className="create-order-input"
                            style={compactInputStyle}
                        />
                            </div>
                            <div className="create-order-field create-order-field--wide">
                        <label htmlFor="create-order-object" style={labelStyle}>
                            <span>Objet</span>
                            <span className="create-order-optional">Optionnel</span>
                        </label>
                        <input
                            id="create-order-object"
                            type="text"
                            value={commentaire}
                            onChange={e => setCommentaire(e.target.value)}
                            placeholder="Ex : Commande médicaments patient Dupont"
                            className="create-order-input"
                            style={compactInputStyle}
                        />
                            </div>
                            <div className="create-order-status" aria-label="Statut initial : À traiter">
                                <span>Statut initial</span>
                                <strong><i aria-hidden="true" />À traiter</strong>
                            </div>
                        </div>
                    </section>

                    <section className="create-order-section" aria-labelledby="create-order-content-title">
                        <div className="create-order-section-heading">
                            <span>02</span>
                            <div>
                                <h3 id="create-order-content-title">Contenu et pièces jointes</h3>
                                <p>Ajoutez les informations utiles au traitement de la commande.</p>
                            </div>
                        </div>
                        <div className="create-order-grid create-order-grid--content">
                            <div className="create-order-field">
                            <label htmlFor="create-order-notes" style={labelStyle}>
                                <span>Commentaire</span>
                                <span className="create-order-optional">Optionnel</span>
                            </label>
                            <textarea
                                id="create-order-notes"
                                value={notes}
                                onChange={e => setNotes(e.target.value)}
                                placeholder="Notes de l'opérateur..."
                                rows={3}
                                className="create-order-input"
                                style={{ ...inputStyle, resize: "vertical", lineHeight: "1.6", minHeight: "118px" }}
                            />
                            </div>
                            <div className="create-order-field">
                            <div style={labelStyle}>
                                <span>Pièces jointes</span>
                                <span className="create-order-optional">Optionnel</span>
                            </div>
                            <FileDropZone files={files} onChange={setFiles} maxFileSizeMb={maxFileSizeMb} />
                            </div>
                        </div>
                    </section>

                    <section className="create-order-section" aria-labelledby="create-order-patients-title">
                        <div className="create-order-section-heading">
                            <span>03</span>
                            <div>
                                <h3 id="create-order-patients-title">Patients et médicaments <small>Optionnel</small></h3>
                                <p>Renseignez directement les lignes à préparer si elles sont déjà connues.</p>
                            </div>
                        </div>
                        <div className="create-order-patient-frame">
                            <div style={{ minWidth: "1020px", height: "100%" }}>
                                <PatientForm
                                    patients={patients}
                                    onChange={setPatients}
                                    medicamentStrategy={medicamentStrategy}
                                    ehpadId={ehpadId ? Number(ehpadId) : null}
                                />
                            </div>
                        </div>
                    </section>

                    {error && (
                        <div role="alert" className="create-order-error">{error}</div>
                    )}
                </div>

                {/* Footer */}
                <div className="create-order-footer">
                    <p><span aria-hidden="true">✓</span> Les champs marqués d’un astérisque sont obligatoires.</p>
                    <div>
                    <button
                        type="button"
                        onClick={() => onClose()}
                        disabled={saving}
                        className="create-order-button create-order-button--secondary"
                    >
                        Annuler
                    </button>
                    <button
                        type="button"
                        onClick={handleSubmit}
                        disabled={saving || !ehpadId}
                        className="create-order-button create-order-button--primary"
                    >
                        {saving ? "Création..." : "Créer la commande"}
                    </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
